var _excluded = ["parentId", "className", "columnTitle", "renderItem", "columnWidth", "children", "rowHeight", "listHeight", "emptyRender", "emptyContent", "virtualized", "columnWidthAuto"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React, { useEffect, useMemo, useRef } from 'react';
import { classnames, isUndefined } from '@okee-uikit/react-utils';
import { getElementPosition, getElementScroll, useScrollTo } from '@okee-uikit/react-hooks';
import { usePrefixCls, useDirection, useLocale } from '@okee-uikit/react-config';
import { Right, Loading as LoadingIcon } from '../../../common/icons';
import { useCascader, useCascaderActive } from '../../../hooks/cascader';
import { AutoSizedList } from '../../../units/auto-sized-list';
import { Loading } from '../../loading';
import { CascaderItem } from './CascaderItem';
import { CascaderItemAll } from './CascaderItemAll';
import { Empty } from '../../empty';
var CascaderListItemType;

(function (CascaderListItemType) {
  CascaderListItemType[CascaderListItemType["SHOWALL"] = 0] = "SHOWALL";
  CascaderListItemType[CascaderListItemType["ITEM"] = 1] = "ITEM";
  CascaderListItemType[CascaderListItemType["BottomSot"] = 2] = "BottomSot";
  CascaderListItemType[CascaderListItemType["Loading"] = 3] = "Loading";
})(CascaderListItemType || (CascaderListItemType = {}));

export function CascaderColumn(props) {
  var _a;

  var parentId = props.parentId,
      className = props.className,
      columnTitle = props.columnTitle,
      renderItem = props.renderItem,
      columnWidth = props.columnWidth,
      children = props.children,
      _props$rowHeight = props.rowHeight,
      rowHeight = _props$rowHeight === void 0 ? 34 : _props$rowHeight,
      _props$listHeight = props.listHeight,
      listHeight = _props$listHeight === void 0 ? 192 : _props$listHeight,
      emptyRender = props.emptyRender,
      emptyContent = props.emptyContent,
      _props$virtualized = props.virtualized,
      virtualized = _props$virtualized === void 0 ? true : _props$virtualized,
      columnWidthAuto = props.columnWidthAuto,
      extra = _objectWithoutProperties(props, _excluded);

  var direction = useDirection();
  var locale = useLocale('cascader.empty');
  var boxRef = useRef(null);
  var cascader = useCascader();
  var colWidth = columnWidth || cascader.columnWidth;
  var info = cascader.getItemInfo(parentId) || {};
  var expandedIds = useCascaderActive();
  var hasMore = useMemo(function () {
    var _a;

    return info.hasMore && cascader.loadMore && ((info.showAll ? 1 : 0) + (((_a = info.children) === null || _a === void 0 ? void 0 : _a.length) || 0)) * rowHeight > listHeight;
  }, [info, rowHeight, listHeight, cascader]);
  var cls = classnames(usePrefixCls('cascader-column', {
    rtl: direction === 'rtl'
  }), className);
  var listCls = usePrefixCls('cascader-column-list');
  var innerCls = usePrefixCls('cascader-column-inner', {
    loading: info.loading
  });
  var activeCls = usePrefixCls('cascader-active-item');
  var titleCls = usePrefixCls('cascader-column-title');
  var arrowCls = usePrefixCls('cascader-item-arrow');
  var labelCls = usePrefixCls('cascader-item-label');
  var loadingCls = usePrefixCls('cascader-column-loading');

  var renderTitle = function renderTitle() {
    if (!columnTitle) {
      return null;
    }

    return React.createElement("div", {
      className: titleCls
    }, columnTitle);
  };

  var renderDefaultItem = function renderDefaultItem(item) {
    var label = item.label,
        itemChildren = item.children,
        needLoad = item.needLoad;
    var hasArrow = needLoad || !isUndefined(itemChildren);
    return React.createElement(React.Fragment, null, React.createElement("div", {
      className: labelCls
    }, label), hasArrow && React.createElement(Right, {
      className: arrowCls
    }));
  };

  var renderCustomItem = function renderCustomItem(item) {
    var itemChildren = item.children,
        needLoad = item.needLoad;
    var hasArrow = needLoad || !isUndefined(itemChildren);
    return React.createElement(React.Fragment, null, renderItem && React.createElement("div", {
      className: labelCls
    }, renderItem(item)), hasArrow && React.createElement(Right, {
      className: arrowCls
    }));
  };

  var getCascaderListItemType = function getCascaderListItemType(index) {
    var _a;

    var topNumber = info.showAll ? 1 : 0;
    var bottomNumber = cascader.bottomSlot ? 1 : 0;
    var childrenLength = ((_a = info.children) === null || _a === void 0 ? void 0 : _a.length) || 0;
    return index === 0 && info.showAll ? CascaderListItemType.SHOWALL : index === childrenLength + topNumber && cascader.bottomSlot ? CascaderListItemType.BottomSot : index === childrenLength + topNumber + bottomNumber && hasMore ? CascaderListItemType.Loading : CascaderListItemType.ITEM;
  };

  var renderCascaderListItem = function renderCascaderListItem(_ref) {
    var key = _ref.key,
        index = _ref.index,
        style = _ref.style;

    var _a, _b;

    var type = getCascaderListItemType(index);

    if (type === CascaderListItemType.SHOWALL) {
      if (!index) {
        return React.createElement(CascaderItemAll, {
          style: style,
          key: key,
          parentId: parentId
        });
      }
    }

    if (type === CascaderListItemType.BottomSot) {
      return React.createElement("div", {
        style: style,
        key: key,
        className: loadingCls
      }, (_a = cascader === null || cascader === void 0 ? void 0 : cascader.bottomSlot) === null || _a === void 0 ? void 0 : _a.call(cascader, cascader.getItemInfo(info.id)));
    }

    if (type === CascaderListItemType.Loading) {
      return React.createElement("div", {
        style: style,
        key: key,
        className: loadingCls
      }, React.createElement(LoadingIcon, {
        style: {
          marginRight: '10px'
        }
      }), "\u52A0\u8F7D\u4E2D");
    }

    var item = (_b = info.children) === null || _b === void 0 ? void 0 : _b[index - (info.showAll ? 1 : 0)];
    if (!item) return null;
    var itemCls = '';

    if (expandedIds.indexOf(item.id) > -1) {
      itemCls = activeCls;
    }

    return React.createElement(CascaderItem, {
      key: key,
      id: item.id,
      className: itemCls,
      style: style
    }, renderItem ? renderCustomItem(item) : renderDefaultItem(item));
  };

  var onScroll = function onScroll(e) {
    cascader.scroll && cascader.scroll(e, cascader.getExtraInfoById(info.id));

    if (e.clientHeight + e.scrollTop >= e.scrollHeight - rowHeight) {
      cascader.scrollBottomLoad(info.id);
    }
  };

  var renderCascaderList = function renderCascaderList() {
    if (info.loading) {
      return React.createElement("div", {
        className: innerCls
      }, React.createElement(Loading.BlockLoading, {
        show: true,
        maskStyle: {
          background: 'none'
        }
      }));
    }

    if (isUndefined(info.children)) {
      return null;
    }

    if (!info.children.length) {
      return emptyRender ? emptyRender(locale) : React.createElement(Empty, {
        content: emptyContent ? emptyContent(locale) : locale
      });
    }

    var rowCount = info.children.length + (info.showAll ? 1 : 0) + (cascader.bottomSlot ? 1 : 0) + (hasMore ? 1 : 0);

    if (!virtualized) {
      return React.createElement("div", {
        className: innerCls
      }, new Array(rowCount).fill(undefined).map(function (item, index) {
        return renderCascaderListItem({
          index: index,
          key: index
        });
      }));
    }

    return React.createElement(AutoSizedList, {
      listClsName: innerCls,
      rowHeight: rowHeight,
      rowCount: rowCount,
      rowRenderer: renderCascaderListItem,
      onScroll: onScroll
    });
  };

  var _useScrollTo = useScrollTo(boxRef, 'top', function () {
    var container = boxRef.current;

    if (!container) {
      return 0;
    }

    var activeELem = container.querySelector(".".concat(activeCls));

    if (!activeELem) {
      return 0;
    }

    var elemPos = getElementPosition(activeELem);
    var containerPos = getElementPosition(container);
    var scrollTop = getElementScroll(container).scrollTop;

    if (elemPos.top >= containerPos.top && elemPos.bottom <= containerPos.bottom) {
      return scrollTop;
    }

    return elemPos.top - containerPos.top + scrollTop;
  }, 150, function () {
    return !!boxRef.current && !!boxRef.current.querySelector(".".concat(activeCls));
  }),
      startScroll = _useScrollTo.startScroll;

  var listScroll = function listScroll(e) {
    if (virtualized) return;
    var _e$target = e.target,
        clientHeight = _e$target.clientHeight,
        scrollHeight = _e$target.scrollHeight,
        scrollTop = _e$target.scrollTop;
    var scrollParams = {
      clientHeight: clientHeight,
      scrollHeight: scrollHeight,
      scrollTop: scrollTop
    };
    onScroll(scrollParams);
  };

  useEffect(function () {
    startScroll();
  });
  return React.createElement("div", _objectSpread(_objectSpread({}, extra), {}, {
    className: cls,
    style: {
      width: !((_a = info === null || info === void 0 ? void 0 : info.children) === null || _a === void 0 ? void 0 : _a.length) && !info.loading ? '100%' : columnWidthAuto ? 'auto' : "".concat(colWidth, "px"),
      minWidth: columnWidthAuto ? 'auto' : "".concat(colWidth, "px")
    }
  }), renderTitle(), React.createElement("div", {
    className: listCls,
    style: {
      height: listHeight
    },
    ref: boxRef,
    onScroll: listScroll
  }, children, renderCascaderList()));
}